<template>
  <div class="b-t b-grey-70">
    <define-store-address>
      <p>
        {{ store.address1 }}<br>
        <template v-if="store.address2">
          {{ store.address2 }}<br>
        </template>
        {{ store.city }} {{ store.state }}. {{ store.postalcode }}, {{ store.country }}<br>
      </p>
    </define-store-address>
    <div class="relative">
      <base-button
        v-if="enableSelection"
        :aria-label="store.name"
        :aria-pressed="store.storeid === selectedStore?.storeid"
        class="absolute mt-1 p-3 ml-3 mt-2 "
        @click="selectedStore = store"
      >
        <span class="block h-6 w-6 rounded-full p-1 ring ring-grey-40 ring-inset duration peer-focus-within:ring-grey-10 peer-focus-visible:outline-auto !h-4 !w-4">
          <span
            class="block full rounded-full duration bg-brand-1"
            :class="store.storeid === selectedStore?.storeid ? 'op-100 ' : 'op-0'"
          />
        </span>
      </base-button>
      <base-collapse
        class="w-full"
        :class-toggle="[
          'w-full py-4 px-6',
          { 'pl-14 ': enableSelection },
        ]"
      >
        <template #toggle="{ expanded }">
          <span class="flex gap-4 between">
            <span v-html="store.name" class="subtitle-4" />
            <vf-icon :dir="expanded ? 'up' : 'down'" name="chevron" size="md" />
          </span>
          <span class="flex text-sm between c-grey-20">
            <slot name="store-body-collapsed" :store>
              <store-address v-if="showAddressPreview" class="mt-2 " />
              <template v-else>
                <span v-if="qty && store.bopis_enabled === '1'">{{ $t.readyIn24hours }}.</span>
                <span v-else-if="store.sts_enabled === '1'">{{ $t.readyIn3Days }}.</span>
              </template>
              <span>({{ store._distance }} {{ store._distanceuom }})</span>
            </slot>
          </span>
          <span v-if="qty && qty < qtyThreshold" class="text-sm c-grey-20">
            {{ replaceAll($t.qtyLimitLow, { limit: qty }) }}
          </span>
        </template>
        <div
          class="pb-4 space-y-6 px-6"
          :class=" { 'pl-14 ': enableSelection }"
        >
          <address class="text-sm space-y-4">
            <store-address v-if="!showAddressPreview"  />
            <span class="flex items-center gap-2">
              <vf-icon name="location" size="lg" />
              <vf-link :to="`http://maps.google.com/?q=${store.latitude},${store.longitude}`">
                {{ $t.getDirections }}
              </vf-link>
            </span>
            <span class="flex items-center gap-2">
              <vf-icon name="phone" size="lg" />
              <vf-link :to="`tel:${store.phone}`">
                <span class="sr-only">{{ $t.call }}</span>
                {{ store.phone }}
              </vf-link>
            </span>
          </address>
          <div>
            <p class="mb-2 fw-medium">
              {{ $t.storeHours }}
            </p>
            <ul >
              <li>{{ $t.weekday }}: {{ store.m }}</li>
              <li>{{ $t.saturday }}: {{ store.sa }}</li>
              <li>{{ $t.sunday }}: {{ store.su }}</li>
            </ul>
          </div>
          <div v-if="store.store_description">
            <p class="mb-2 fw-medium">
              {{ $t.getStoreInstruction }}
            </p>
            <p >
              {{ store.store_description }}
            </p>
          </div>
          <vf-button
            v-if="$feature.allowProductReservationOnPDP && !isReservationOpen"
            :loading="isReservationOutgoing"
            @click="handleReserve"
          >
            {{ $t.reserve }}
          </vf-button>
        </div>
        <form-robis
          v-if="isReservationOpen"
          ref="robisFormRef"
          :cart-id="cartId"
          class="mt-2 pb-6"
          :store-shipping-address="storeShippingAddress"
          tabindex="-1"
          @success="isReservationOpen = false"
        />
      </base-collapse>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { LocatorSearchItem } from '#types/brandify'
import type { PickupProduct } from '#types/components/product/pickupInStore'

const { item, store } = defineProps<{
  item?: PickupProduct
  store: LocatorSearchItem & { storeInventory?: number }
  enableSelection?: boolean
  showAddressPreview?: boolean
}>()

const emit = defineEmits<{
  error: [value: boolean]
}>()

const [DefineStoreAddress, StoreAddress] = createReusableTemplate()

const selectedStore = defineModel<LocatorSearchItem>()

const robisFormRef = ref()

const { qtyThreshold } = useAppConfig().components.form.bopisStoreSearch
const { $feature } = useNuxtApp()

const qty = computed(() => store.quantity || store.storeInventory)

let cartId: ReturnType<typeof useProductReservation>['cartId']
let isReservationOpen: ReturnType<typeof useProductReservation>['isReservationOpen']
let isReservationOutgoing: ReturnType<typeof useProductReservation>['isReservationOutgoing']
let reserve: ReturnType<typeof useProductReservation>['reserve']
let storeShippingAddress: ReturnType<typeof useProductReservation>['storeShippingAddress']

if (item && $feature.allowProductReservationOnPDP) {
  ({
    cartId,
    isReservationOpen,
    isReservationOutgoing,
    reserve,
    storeShippingAddress
  } = useProductReservation({ item, store }))
}

const handleReserve = async () => {
  try {
    await reserve?.()
    // move focus to form once it appears
    robisFormRef.value.$el.focus()
  }
  catch {
    emit('error', true)
  }
}
</script>
